import type React from 'react'
import { useApiUrl, useNotification, type IResourceComponentsProps } from '@refinedev/core'
import { CustomSearchBar, filterData } from '../../components/shared/CustomSearchBar'
import EditableCell from '../../components/shared/EditableCell'

import {
  useTable, List,
  UrlField
} from '@refinedev/antd'

import {
  Button,
  Checkbox,
  Popconfirm,
  Popover,
  Table,
  Tag,
  theme
} from 'antd'

import { useEffect, useState } from 'react'
import { fetchWithAuth } from '../../fn'
import './style.css'
import { DeleteOutlined } from '@ant-design/icons'

// KEEP IN SYNC with STANDING_SECTION_REGEX in src/server/modules/common/sales.js
// Non-numbered standing sections: defaults flip to postoNumerato=false, checkRestrictions=false
// so the UI matches what the scanner actually uses at runtime.
// NB: "curva" removed 2026-06-23 (see sales.js) — Curva is always numbered seating in our catalog.
// NB: "platea" added 2026-07-06 (see sales.js) — Platea defaults to standing/unnumbered.
const STANDING_SECTION_REGEX = /general admission|\bprato\b|\bfloor\b|standing|\bpit\b|\bga\b|\bplatea\b|non numerat|\bvip\b|\bgold\b/i
const isStandingSection = (section = '', block = ''): boolean => STANDING_SECTION_REGEX.test(`${section} ${block}`)

// KEEP IN SYNC with parseEventDate/isPastEvent in src/server/modules/common/sales.js
// `dataEvento` è una stringa, non una data: gigsberg salva "10 Sep 2026", viagogo
// "Sunday, September 6, 2026 08:35", i record vecchi dello scraping possono avere
// il letterale "undefined". Una data non interpretabile NON è mai "passata".
const MONTH_INDEX: Record<string, number> = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 }

const parseEventDate = (value?: string): Date | null => {
  const raw = String(value ?? '').trim()
  if (!raw) return null

  const short = /^(\d{1,2})\s+([A-Za-z]{3})[a-z]*\s+(\d{4})$/.exec(raw)
  if (short) {
    const month = MONTH_INDEX[short[2].toLowerCase()]
    if (month !== undefined) return new Date(Number(short[3]), month, Number(short[1]))
  }

  const parsed = new Date(raw)
  return isNaN(parsed.getTime()) ? null : parsed
}

// Passato = data precedente a oggi (granularità giorno, ora locale): l'evento di
// oggi resta gestibile, si vende fino a fine giornata.
const isPastEvent = (record: { dataEvento?: string }): boolean => {
  const date = parseEventDate(record?.dataEvento)
  if (!date) return false

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() < today.getTime()
}

const printTable = (updatedTableProps: any, handleCellChange: any, handleDeleteAnnuncio: any, stopLossValues: any, setStopLossValues: any, colorBgContainer: string, colorBorder: string): any => {
  return (
    <Table {...updatedTableProps} className='custom-table'>
      <Table.Column
        title='Evento'
        dataIndex='evento'
        key='evento'
        render={
          (evento: string, record: any) =>
            <div
              style={{ width: '300px', color: record.canceled ? 'red' : 'inherit' }}
            >
              <b>🎫 {evento}</b>
              {isPastEvent(record) && <>{' '}<Tag color='default'>PASSATO</Tag></>}
              <br />
              🗺 {record?.luogo}
              <br />
              📍 {record?.stadium}
              <br />
              🗓️ {record?.dataEvento}
              <br />
              #️⃣ {record?.idEventoNumeric}
              <br />
              <UrlField target='_blank' value={record?.url}>🌐 URL ↗️</UrlField>
              <br />
              {(isPastEvent(record) || record?.canceled || (record.annunci && Object.keys(record.annunci).length === 0)) && (
                <Popconfirm
                  title={isPastEvent(record)
                    ? 'Sei sicuro di voler eliminare questo evento passato?'
                    : 'Sei sicuro di voler eliminare questo evento?'}
                  onConfirm={() => handleDeleteAnnuncio((window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url)}
                  okText='Sì'
                  cancelText='No'
                >
                  <Button
                    type='primary'
                    danger
                    icon={<DeleteOutlined />}
                    title={isPastEvent(record) ? 'Elimina evento passato' : 'Elimina evento'}
                    onClick={(e) => { e.stopPropagation() }}
                    size='middle'
                  />
                </Popconfirm>
              )}
            </div>
        }

      />
      <Table.Column
        title='Annunci'
        dataIndex='annunci'
        key='annunci'
        render={(annunci: object, record: any) => {
          if (Object.values(annunci ?? {}).length === 0) return null
          return (
            <Table
              dataSource={Object.values(annunci ?? {})}
              columns={[
                {
                  title: 'Annuncio',
                  dataIndex: 'idAnnuncio',
                  key: 'idAnnuncio',
                  width: 150,
                  render: (idAnnuncio: number, annuncio: any) => (

                    <div style={{ opacity: Number(annuncio.available) ? 1 : 0.3, color: annuncio.canceled ? 'red' : 'inherit' }}>

                      <Popover
                        content={
                          <>
                            <div>
                              Section: <Tag color='blue'>{annuncio?.section}</Tag>
                              <br />
                              Block: <Tag color='blue'>{annuncio?.block}</Tag>
                              <br />
                              Row: <Tag color='blue'>{annuncio?.row}</Tag>
                              <br />
                              Restriction: {annuncio?.restriction && <Tag color='blue'>{annuncio?.restriction}</Tag>}
                              <br />
                              #️⃣ Disponibili: <Tag color='blue'> {annuncio.available}</Tag>
                              <br />
                              #️⃣ ID Annuncio: <Tag> {idAnnuncio}</Tag>
                            </div>

                            <pre>
                              {annuncio?.message}
                            </pre>

                            <div>
                              <b>Last Edited:</b> {annuncio?.lastEdited ? new Date(annuncio.lastEdited).toLocaleString() : 'N/A'}
                            </div>
                            <div>
                              <b>Last Scan:</b> {record?.lastScan ? new Date(record.lastScan).toLocaleString() : 'N/A'}
                            </div>
                            {record?.lastScanError && (
                              <div style={{ color: 'red', maxWidth: 400, wordBreak: 'break-word' }}>
                                <b>Last Scan Error:</b> {record.lastScanError}
                              </div>
                            )}

                          </>
                        }
                        title='Dati Annuncio'
                      >

                        <b>{annuncio.available}</b>
                        <span> x </span>
                        <b>{
                          Array.from(
                            new Set(
                              [annuncio?.section, annuncio?.block, annuncio?.row]
                            )
                          )
                            .join(' ')
                            .replace('Row not specified', '')
                            .replace('Block not specified', '')
                            .replace('Row:', '')
                        }
                        </b>
                        ❔
                        <div>
                          <i>{annuncio?.restriction}</i>
                        </div>
                      </Popover>
                      <div style={{ display: 'flex', justifyContent: 'right' }}>
                        {annuncio.canceled && (
                          <Popconfirm
                            title='Sei sicuro di voler eliminare questo annuncio?'
                            onConfirm={() => handleDeleteAnnuncio((window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                            okText='Sì'
                            cancelText='No'
                          >
                            <Button
                              type='primary'
                              danger
                              icon={<DeleteOutlined />}
                              onClick={(e) => { e.stopPropagation() }}
                              size='small'
                            />
                          </Popconfirm>
                        )}
                      </div>
                    </div>)
                },
                {
                  title: 'Price',
                  dataIndex: 'price',
                  key: 'price',
                  width: 100,
                  render:
                    (price: number, annuncio: any) => (
                      <div style={{ textAlign: 'center', opacity: Number(annuncio.available) ? 1 : 0.3 }}>
                        <Tag color='green'>€{Number(price).toFixed(2)}</Tag>
                      </div>)
                },
                {
                  title: 'Proceeds',
                  dataIndex: 'proceeds',
                  key: 'proceeds',
                  width: 100,
                  render:
                    (proceeds: number, annuncio: any) => (
                      <div style={{ textAlign: 'center', opacity: Number(annuncio.available) ? 1 : 0.3 }}>
                        <Tag color='gold'>€{proceeds}</Tag>
                      </div>)
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          Valore minimo di ribasso del prezzo, di default è 3x il face value
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>StopLoss❔</span>
                    </Popover>
                  ),
                  dataIndex: ['config', 'stopLoss'],
                  key: 'stopLoss',
                  width: 110,
                  render: (stopLoss: number, annuncio: any) => (
                    <EditableCell
                      value={stopLoss ?? (parseFloat(annuncio.faceValue) * 3).toFixed(2)}
                      onChange={(value) => handleCellChange(value, 'stopLoss', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                      style={{ backgroundColor: stopLoss ? colorBgContainer : colorBorder, opacity: Number(annuncio.available) ? 1 : 0.3 }}
                    />
                  )
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          Valore massimo di rialzo del prezzo, di default è 15x il face value
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>StopGain❔</span>
                    </Popover>
                  ),
                  dataIndex: ['config', 'stopGain'],
                  key: 'stopGain',
                  width: 110,
                  render: (stopGain: number, annuncio: any) => (
                    <EditableCell
                      value={stopGain ?? (parseFloat(annuncio.faceValue) * 15).toFixed(2)}
                      onChange={(value) => handleCellChange(value, 'stopGain', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                      style={{ backgroundColor: stopGain ? colorBgContainer : colorBorder, opacity: Number(annuncio.available) ? 1 : 0.3 }}
                    />
                  )
                },
                {
                  title: 'Face Value',
                  dataIndex: 'faceValue',
                  key: 'faceValue',
                  width: 100,
                  render:
                    (faceValue: number, annuncio: any) => (
                      <div style={{ textAlign: 'center', opacity: Number(annuncio.available) ? 1 : 0.3 }}>
                        <Tag color='cyan'>€{faceValue}</Tag>
                      </div>
                    )
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          <p><Checkbox indeterminate /> Valore di default: "è un posto numerato"</p>
                          <p><Checkbox checked /> "è un posto numerato"</p>
                          <p><Checkbox checked={false} /> "non è un posto numerato"</p>
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>Posto Numerato❔</span>
                    </Popover>
                  ),

                  dataIndex: ['config', 'postoNumerato'],
                  key: 'postoNumerato',
                  width: 110,
                  render: (postoNumerato: boolean, annuncio: any) => {
                    const standing = isStandingSection(annuncio.section, annuncio.block)
                    const effective = postoNumerato ?? !standing
                    return (
                      <EditableCell
                        value={effective}
                        inputType='checkbox'
                        onChange={(value) => handleCellChange(value, 'postoNumerato', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                        style={{ opacity: Number(annuncio.available) ? 1 : 0.3 }}
                      />
                    )
                  }
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          <p><Checkbox checked /> Attivo: il bot è attivo su questo annuncio.</p>
                          <p><Checkbox checked={false} /> Disattivo: il bot non è attivo su questo annuncio.</p>
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>Attivo❔</span>
                    </Popover>
                  ),
                  dataIndex: ['config', 'enabled'],
                  key: 'enabled',
                  width: 80,
                  render: (enabled: boolean, annuncio: any) => (
                    <EditableCell
                      value={enabled ?? true}
                      inputType='checkbox'
                      onChange={(value) => handleCellChange(value, 'enabled', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                      style={{ opacity: Number(annuncio.available) ? 1 : 0.3 }}
                    />
                  )
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          <p><Checkbox checked /> Attivo: Dati tutti i competitor per l'evento, se attivo posiziona 1 centesimo sotto al miglior competitor</p>
                          <p><Checkbox checked={false} /> Disattivo: Confronta con i competitor a partire da quelli della stessa categoria</p>
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>Primo In Assoluto❔</span>
                    </Popover>
                  ),
                  dataIndex: ['config', 'considerAllCompetitors'],
                  key: 'considerAllCompetitors',
                  width: 100,
                  render: (considerAllCompetitors: boolean, annuncio: any) => (
                    <EditableCell
                      value={considerAllCompetitors ?? false}
                      inputType='checkbox'
                      onChange={(value) => handleCellChange(value, 'considerAllCompetitors', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                      style={{ opacity: Number(annuncio.available) ? 1 : 0.3 }}
                    />
                  )
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          <p><Checkbox checked /> Confronta anche il blocco, quindi biglietti con blocco diverso non vengono considerati.</p>
                          <p><Checkbox checked={false} /> Non confronta il blocco, quindi considera solo la categoria per determinare gli annunci concorrenti. (default)</p>
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>Confronta Blocco❔</span>
                    </Popover>
                  ),
                  dataIndex: ['config', 'checkBlock'],
                  key: 'checkBlock',
                  width: 100,
                  render: (checkBlock: boolean, annuncio: any) => (
                    <EditableCell
                      value={checkBlock ?? false}
                      inputType='checkbox'
                      onChange={(value) => handleCellChange(value, 'checkBlock', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                      style={{ opacity: Number(annuncio.available) ? 1 : 0.3 }}
                    />
                  )
                },
                {
                  title: (
                    <Popover
                      content={
                        <pre>
                          <p><Checkbox checked /> Confronta anche le restrizioni, esempio "Visibilita Limitata"</p>
                          <p><Checkbox checked={false} /> Non confronta le restrizioni. (default)</p>
                        </pre>
                      }
                      title='Legenda'
                    >
                      <span>Confronta Restrizioni❔</span>
                    </Popover>
                  ),
                  dataIndex: ['config', 'checkRestrictions'],
                  key: 'checkRestrictions',
                  width: 100,
                  render: (checkRestrictions: boolean, annuncio: any) => {
                    const standing = isStandingSection(annuncio.section, annuncio.block)
                    const effective = checkRestrictions ?? !standing
                    return (
                      <EditableCell
                        value={effective}
                        inputType='checkbox'
                        onChange={(value) => handleCellChange(value, 'checkRestrictions', (window.location.pathname.includes('gigsberg') || window.location.pathname.includes('viagogo')) ? record.idEventoNumeric : record.url, annuncio.idAnnuncio)}
                        style={{ opacity: Number(annuncio.available) ? 1 : 0.3 }}
                      />
                    )
                  }
                }
              ]}
              pagination={false}
              rowKey='idAnnuncio'
              scroll={{ x: 'max-content' }}
            />
          )
        }}
      />
    </Table>
  )
}
export const SalesList: React.FC<IResourceComponentsProps> = () => {
  const { token } = theme.useToken()
  const { tableProps, tableQuery } = useTable({
    syncWithLocation: true,

    pagination: {
      mode: 'off'
    }
  })

  const { open } = useNotification()

  const API_URL = useApiUrl()

  const [stopLossValues, setStopLossValues] = useState<Record<number, string>>({})

  const handleCellChange = async (value: string | boolean, field: string, url: string, annuncioId: number): Promise<void> => {
    const route = window.location.pathname.split('/').pop() ?? ''

    try {
      console.log(window.location.pathname.replace('/', ''))
      const data = await fetchWithAuth(`${API_URL}/${route}/update-subtable-cell`, {
        method: 'POST',
        body: { annuncioId, url, field, value } // stopLoss: value ? Number(value) : null }
      })

      if (data?.error) {
        open?.({ message: `Failed to update ${field}!`, description: data.error, type: 'error' })
      } else {
        open?.({ message: `${field} updated successfully!`, type: 'success' })
      }

      await tableQuery.refetch()
    } catch (error) {
      console.error(`Failed to update${field}:`, error)
      open?.({ message: `Failed to update ${field}!`, type: 'error' })
    }
  }

  const handleDeleteAnnuncio = async (url: string, annuncioId?: number): Promise<void> => {
    const route = window.location.pathname.split('/').pop() ?? ''

    try {
      const data = await fetchWithAuth(`${API_URL}/${route}/delete-annuncio`, {
        method: 'DELETE',
        body: { annuncioId, url }
      })

      if (data?.error) {
        open?.({ message: 'Failed to delete annuncio!', description: data.error, type: 'error' })
      } else {
        open?.({ message: 'Annuncio deleted successfully!', type: 'success' })
      }

      await tableQuery.refetch()
    } catch (error) {
      console.error('Failed to delete annuncio:', error)
      open?.({ message: 'Failed to delete annuncio!', type: 'error' })
    }
  }

  const handleDeleteCanceled = async (): Promise<void> => {
    const route = window.location.pathname.split('/').pop() ?? ''

    try {
      const data = await fetchWithAuth(`${API_URL}/${route}/delete-canceled`, {
        method: 'DELETE'
      })

      if (data?.error) {
        open?.({ message: 'Failed to delete canceled items!', description: data.error, type: 'error' })
      } else {
        open?.({ message: 'Canceled items deleted successfully!', type: 'success' })
      }

      await tableQuery.refetch()
    } catch (error) {
      console.error('Failed to delete canceled items:', error)
      open?.({ message: 'Failed to delete canceled items!', type: 'error' })
    }
  }

  const handleDeletePast = async (): Promise<void> => {
    const route = window.location.pathname.split('/').pop() ?? ''

    try {
      const data = await fetchWithAuth(`${API_URL}/${route}/delete-past`, {
        method: 'DELETE'
      })

      if (data?.error) {
        open?.({ message: 'Failed to delete past events!', description: data.error, type: 'error' })
      } else {
        const deletedCount = Number(data?.deleted?.length ?? 0)
        open?.({ message: `${deletedCount} evento/i passato/i eliminato/i!`, type: 'success' })
      }

      await tableQuery.refetch()
    } catch (error) {
      console.error('Failed to delete past events:', error)
      open?.({ message: 'Failed to delete past events!', type: 'error' })
    }
  }

  const [query, setQuery] = useState<any>()
  const updatedTableProps = {
    ...tableProps,
    dataSource: filterData(tableProps.dataSource, query)
  }

  const [hasCanceledItems, setHasCanceledItems] = useState(false)

  useEffect(() => {
    const checkCanceledItems = (): boolean => {
      if (!updatedTableProps.dataSource) {
        return false
      }
      return updatedTableProps.dataSource.some((event: { canceled: any, annunci?: Record<string, unknown> | ArrayLike<unknown> | null }) => {
        if (event.canceled) return true
        return Object.values(event.annunci ?? {}).some((annuncio: any) => annuncio.canceled)
      })
    }

    setHasCanceledItems(checkCanceledItems())
  }, [updatedTableProps.dataSource])

  // Contato sull'insieme NON filtrato: il bottone elimina tutti gli eventi passati
  // (come "Elimina tutti i canceled"), non solo quelli visibili dopo la ricerca.
  const [pastEventsCount, setPastEventsCount] = useState(0)

  useEffect(() => {
    setPastEventsCount((tableProps.dataSource ?? []).filter((event: any) => isPastEvent(event)).length)
  }, [tableProps.dataSource])

  return (
    <List
      title={
        <>
          <h2>{'🤖 ' + window.location.pathname.replace('/', '').toUpperCase()}</h2>
        </>
      }
      headerButtons={({ defaultButtons }) => (
        <>
          <Popconfirm
            title={`Eliminare ${pastEventsCount} evento/i con data passata?`}
            onConfirm={() => { void handleDeletePast() }}
            okText='Sì'
            cancelText='No'
          >
            <Button
              type='primary'
              danger
              disabled={!pastEventsCount}
              onClick={(e) => { e.stopPropagation() }}
              size='middle'
            >
              {`Elimina Eventi Passati (${pastEventsCount})`}
            </Button>
          </Popconfirm>
          <Popconfirm
            title='Are you sure you want to delete all canceled items?'
            onConfirm={() => { void handleDeleteCanceled() }}
            okText='Sì'
            cancelText='No'
          >
            <Button
              type='primary'
              danger={hasCanceledItems}
              disabled={!hasCanceledItems}
              onClick={(e) => { e.stopPropagation() }}
              size='middle'
            >
              Elimina Tutti i Canceled
            </Button>
          </Popconfirm>
          <CustomSearchBar
            onFilter={setQuery}
          />
        </>
      )}
    >
      {printTable(updatedTableProps, handleCellChange, handleDeleteAnnuncio, stopLossValues, setStopLossValues, token.colorBgContainer, token.colorBorder)}
    </List>
  )
}
